import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { RootState } from '../../store';
import { selectStateError, selectStateLoading, selectStates, fetchStates, createState, editState, setStateName, setStateStatus, resetForm, selectStatePagination, toggleStateStatus } from "./stateSlice"
import { useAppDispatch } from '../../store';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePermission } from '../../components/common/PermissionGuard';
import PageWrapper from '../../components/common/PageWrapper';
import { State } from './state';
import { fetchUserById } from '../../feature/user/userSlice';
import dayjs from "dayjs";
import SearchBar from '../../components/searchbar';
import Pagination from '../../components/common/Pagination';
import ChangeStatusConfirmation from "../../components/common/__tests__/ChangeStatusConfirmation";

const States: React.FC = () => {
  const dispatch = useAppDispatch();
  const states = useSelector(selectStates);
  const loading = useSelector(selectStateLoading);
  const error = useSelector(selectStateError);
  const pagination = useSelector(selectStatePagination)
  // const filters = useSelector(selectStagesFilters);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [stateName, setStateName] = useState('');
  const [stateStatus, setStateStatus] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStateForStatus, setSelectedStateForStatus] = useState<State | null>(null);

  // Permissions
  const canCreate = usePermission('states', 'create');
  const canUpdate = usePermission('states', 'update');
  const canDelete = usePermission('states', 'delete');
  const canRead = usePermission('states', 'read');

  useEffect(() => {
    console.log("Fetching states...")
    dispatch(fetchStates({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  useEffect(() => {
    console.log("States data:", states);
    console.log("pagination:", pagination);
  },[states,pagination]);

  const handleCreateState = () => {
    dispatch(resetForm());
    setSelectedState(null);
    setFormMode('create');
    setStateName('');
    setStateStatus(1);
    setIsFormOpen(true);
  };

  const handleEditState = (state: State) => {
    setStateName(state.state_name);
    setStateStatus(state.status);
    setSelectedState(state);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleFormSubmit = () => {
    const formData = {
      state_name: stateName,
      status: stateStatus
    };
    if (formMode === 'create') {
      dispatch(createState(formData));
    } else if (selectedState) {
      dispatch(editState({ id: selectedState.id!, data: formData }));
    }
    setIsFormOpen(false);
    setStateName('');
    setStateStatus(1);
  };

  const toggleStatus = async (city: City) => {
    const newStatus = city.status === 1 ? 0 : 1;
    try {
      await dispatch(toggleStateStatus({
        id: city.cityId,
        status: newStatus
      })).unwrap();
      
      dispatch(fetchStates({
        page: currentPage,
        limit: itemsPerPage
      }));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };
  
  
  return (
    <PageWrapper >
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">States Management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage states for your application
          </p>
        </div>
        {canCreate && (
          <button
            onClick={handleCreateState}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create State
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              {/* <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search states..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div> */}
              <SearchBar
                placeholder="Search states..."
                value={searchTerm}
                // onChange={setSearchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Toggle */}
            {/* <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button> */}
          </div>

          {/* Filters Panel */}
          {/* {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Categories</option>
                    {STAGE_CATEGORIES.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Statuses</option>
                    {STAGE_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearAllFilters}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )} */}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        </div>
      )}

      {/* Stages Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    State Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Updated On
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Updated By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {states.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No states found. {canCreate && 'Create your first state to get started.'}
                    </td>
                  </tr>
                ) : (
                  states.filter(state => state.state_name?.toLowerCase().includes(searchTerm.toLowerCase())).map((state) => (
                    <tr key={state.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {state.state_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {(() => {
                          // Show createdOn if record was never updated (bulk uploaded)
                          // Otherwise show updatedOn
                          const displayDate = state?.updatedOn && 
                            state?.createdOn && 
                            dayjs(state.updatedOn).isAfter(dayjs(state.createdOn))
                            ? state.updatedOn 
                            : state?.createdOn || state?.updatedOn;
                          return displayDate ? dayjs(displayDate).format("DD/MM/YYYY HH:mm") : 'N/A';
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {/* {state.updated_by || 'N/A'} */}
                        {state?.user_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer" onClick={() => {
                        setSelectedStateForStatus(color)
                        setIsStatusModalOpen(true)
                      }}>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          state.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {state.status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canUpdate && (
                          <button
                            onClick={() => handleEditState(state)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* State Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {formMode === 'create' ? 'Create State' : 'Edit State'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State Name
                  </label>
                  <input
                    type="text"
                    value={stateName}
                    onChange={(e) => setStateName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter state name"
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={stateStatus}
                    onChange={(e) => setStateStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div> */}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {formMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>

    {isStatusModalOpen && selectedStateForStatus && (
      <ChangeStatusConfirmation
        open={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setSelectedStateForStatus(null);
        }}
        onConfirm={() => {
          toggleStatus(selectedStateForStatus);
          setIsStatusModalOpen(false);
          setSelectedStateForStatus(null);
        }}
        title="Change Status"
        message={`Are you sure you want to change status to ${selectedStateForStatus.status === 1 ? 'Inactive' : 'Active'}?`}
      />
    )}

    {pagination && pagination.total > 0 && (
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={(page) => {
          setCurrentPage(page);
          dispatch(fetchStates({ page, limit: itemsPerPage }));
        }}
        onItemsPerPageChange={(limit) => {
          setItemsPerPage(limit);
          setCurrentPage(1);
          dispatch(fetchStates({ page: 1, limit }));
        }}
        showItemsPerPage={true}
      />
    )}

    </PageWrapper>
  );
};

export default States;
